import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, flatMap, map, mergeMap, shareReplay } from "rxjs/operators";
import { BlogArticle } from './blog-article.model';
import { BlogAuthor } from './blog-author.model';
import { BookOffer } from './book-offer.model';
import { compareDesc, formatDistance, parseISO, parseJSON, format } from 'date-fns';
import { SPORTS } from './sports';
import { LocalisationService } from './localisation.service';
import { HoneycombService } from './honeycomb.service';
import { GeneralService } from './general.service';
import { UserLocationData } from "./user-location-data.model";
import { environment } from 'src/environments/environment';
import { Match } from './match.model';
import { LandingPage } from './landing-page.model';

// const api = require("@opentelemetry/api");
// const tracer = require("tracing.ts");

@Injectable({
	providedIn: 'root'
})
export class StatsInsiderBlogService {

	readKey = "kIszTMwQkliPYn5yOQCHtMAZoW76qEs5Kp8Kr2jxoXf2oIkMch";
	bucketSlug = "ciphercms-production";
	adhesiveBannersVisible = true;

	autoFeatureSports = ["EPL", "NBA", "NFL", /* "AFL", "NRL", "BBL", "SOO"*/];

	userLocationData$: Observable<UserLocationData> = this.http.get<any>(`${environment.dimersGeoDomain}/v1/get_geolocation`)
		.pipe(
			map((response: any) => {
				if (response) {
					return (response as UserLocationData);
				} else if (response.errors) {
					throw new Error(response.errors[0].message)
				} else {
					throw new Error("UNEXPECTED_FORMAT_OR_ERROR");
				}
			}),
			shareReplay(1),
			catchError(this.handleError<UserLocationData>())
		);

	constructor(
		private http: HttpClient,
		private localisationService: LocalisationService,
		private honeycomb: HoneycombService,
		private generalService: GeneralService,
	) { }

	disableAdhesiveBanners(): void {
		this.adhesiveBannersVisible = false;
	}

	getArticle(slug: string, revisionID?: string): Observable<BlogArticle> {
		console.log('getArticle');
		let rno = Math.random()
		console.time('getArticle' + rno)
		const query = {
			"type": "stats-insider-articles",
			"slug": slug,
		};
		if (revisionID) {
			return this.http.get<any>(`https://api.cosmicjs.com/v3/buckets/${this.bucketSlug}/objects?`
				+ `query=${JSON.stringify(query).replace(/\{/g, "%7B").replace(/\}/g, "%7D")}&limit=1&props=id`
				+ `&read_key=${this.readKey}&status=any&depth=1`)
			.pipe(
				flatMap((response1: any) => {
					console.log("gotten article:")
					console.timeLog('getArticle' + rno)
					if (response1.objects && response1.objects.length > 0) {
						const articleID = response1.objects[0].id;
						return this.http.get<any>(`https://api.cosmicjs.com/v3/buckets/${this.bucketSlug}/objects/${articleID}/revisions/${revisionID}`
							+ `?read_key=${this.readKey}`)
					} else if (response1.errors) {
						throw new Error(response1.errors[0].message)
					} else {
						throw new Error("UNEXPECTED_FORMAT_OR_ERROR");
					}
				}),
				map((response2: any) => {
					if (response2.revision) {
						console.log("gotten revision:");
						console.timeLog('getArticle' + rno)
						const o = response2.revision;
						return {
							// TODO deal with autocon case
							article_category: "dimers_content" as "dimers_content",
							author: o.metadata.author.title,
							authors: [{
								id: o.metadata.author.id,
								last_name: (o.metadata.author.metadata.author_first_name && o.metadata.author.title.includes(o.metadata.author.metadata.author_first_name)) ? o.metadata.author.title.replace(o.metadata.author.metadata.author_first_name, "").trim() : o.metadata.author.title,
								slug: o.metadata.author.slug,
								first_name: (o.metadata.author.metadata.author_first_name && o.metadata.author.title.includes(o.metadata.author.metadata.author_first_name)) ? o.metadata.author.metadata.author_first_name : "",
								social_username: o.metadata.author.metadata.twitter_username,
								description: o.metadata.author.content,
								short_bio: o.metadata.author.content,
								social_summary_title: o.metadata.author.title + " - Sports Contributor",
								social_summary_description: `The latest articles by ${o.metadata.author.title} on Stats Insider. Read data-driven sports content and news from the home of sports predictions.`,
								thumbnail: {
									dynamic_url: o.metadata.author.metadata.author_thumbnail.imgix_url,
								},
							}],
							content_description: o.content,
							created_at: o.created_at,
							featured_article: null,
							id: o.id,
							published_date: null,
							last_updated: null,
							published_date_readable: "Unpublished",
							unpublished_at: o.unpublish_at,
							short_title: o.title,
							slug: o.slug,
							socialThumbnail: {
								url: o.metadata.hero_image.url,
							},
							social_summary_description: o.metadata.preview_text,
							social_summary_title: o.title + "",
							sport_betting_category: null,
							summarized_description: o.metadata.preview_text,
							tags: o.metadata.categories.map(c => ({
								name: c,
								slug: c,
							})),
							thumbnail: {
								dynamic_url: o.metadata.hero_image.imgix_url,
								url: o.metadata.hero_image.url,
							},
							title: o.title,
							faqs: [],
							show_faq: false,
						};
					} else if (response2.errors) {
						throw new Error(response2.errors[0].message)
					} else {
						throw new Error("UNEXPECTED_FORMAT_OR_ERROR");
					}
				}),
				catchError(this.handleError<BlogArticle>())
			)
		} else {
			let o: any;
			return this.http.get<any>(`https://api.cosmicjs.com/v3/buckets/${this.bucketSlug}/objects?`
				+ `query=${JSON.stringify(query).replace(/\{/g, "%7B").replace(/\}/g, "%7D")}`
				+ `&limit=1&read_key=${this.readKey}&depth=1`)
			.pipe(
				mergeMap((mainResponse: any) => {
					console.log("gotten article id:")
					console.timeLog('getArticle' + rno)
					if (mainResponse.objects && mainResponse.objects.length > 0) {
						o = mainResponse.objects[0];
						const articleID = o.id;
						return this.http.get<any>(`https://api.cosmicjs.com/v3/buckets/${this.bucketSlug}/objects/${articleID}/revisions?limit=1&sort=created_at&query=%7B"status"%3A"published"%7D&read_key=${this.readKey}`)
					} else if (mainResponse.errors) {
						throw new Error(mainResponse.errors[0].message)
					} else {
						throw new Error("UNEXPECTED_FORMAT_OR_ERROR");
					}
				}),
				map((revisionsResponse: any) => {
					console.log("gotten article revision:")
					console.timeLog('getArticle' + rno)
					if (revisionsResponse.revisions && revisionsResponse.revisions.length > 0) {
						const earliestPublished = revisionsResponse.revisions[0].created_at;
						{
							return {
								// TODO deal with autocon case
								article_category: "dimers_content" as "dimers_content",
								author: o.metadata.author.title,
								authors: [{
									id: o.metadata.author.id,
									last_name: (o.metadata.author.metadata.author_first_name && o.metadata.author.title.includes(o.metadata.author.metadata.author_first_name)) ? o.metadata.author.title.replace(o.metadata.author.metadata.author_first_name, "").trim() : o.metadata.author.title,
									slug: o.metadata.author.slug,
									first_name: (o.metadata.author.metadata.author_first_name && o.metadata.author.title.includes(o.metadata.author.metadata.author_first_name)) ? o.metadata.author.metadata.author_first_name : "",
									social_username: o.metadata.author.metadata.twitter_username,
									description: o.metadata.author.content,
									short_bio: o.metadata.author.content,
									social_summary_title: o.metadata.author.title + " - Sports Contributor",
									social_summary_description: `The latest articles by ${o.metadata.author.title} on Stats Insider. Read data-driven sports content and news from the home of sports predictions.`,
									thumbnail: {
										dynamic_url: o.metadata.author.metadata.author_thumbnail.imgix_url,
									},
								}],
								content_description: o.content,
								created_at: earliestPublished,
								featured_article: null,
								id: o.id,
								published_date: earliestPublished,
								last_updated: o.published_at,
								published_date_readable: formatDistance(parseJSON(earliestPublished), new Date()) + " ago",
								unpublished_at: o.unpublish_at,
								short_title: o.title,
								slug: o.slug,
								socialThumbnail: {
									url: o.metadata.hero_image.url,
								},
								social_summary_description: o.metadata.preview_text,
								social_summary_title: o.title + "",
								sport_betting_category: null,
								summarized_description: o.metadata.preview_text,
								tags: o.metadata.categories.map(c => ({
									name: c,
									slug: c,
								})),
								thumbnail: {
									dynamic_url: o.metadata.hero_image.imgix_url,
									url: o.metadata.hero_image.url,
								},
								title: o.title,
								faqs: [],
								show_faq: false,
							}
						}
					} else if (revisionsResponse.errors) {
						throw new Error(revisionsResponse.errors[0].message)
					} else {
						throw new Error("UNEXPECTED_FORMAT_OR_ERROR");
					}
				}),
				catchError(this.handleError<BlogArticle>())
			)
		}
	}

	getStateId(state: string): Observable<string> {
        // TODO
		return of("");
    }

	getBestBooksById(stateId: string): Observable<Record<string, any>> {
        // TODO
		return of({});
    }

    getSportsBooksById(stateId: string): Observable<Array<Record<string, any>>> {
        // TODO
		return of([]);
    }

    getBestBooksPromos(stateId: string): Observable<Array<Record<string, any>>> {
        // TODO
		return of([])
    }

	getCosmicOffersByStateId(stateId: string) {
		// TODO
		return of([]);
	}

	getSportsbookReview(stateId: string) {
		// TODO
		return of({});
	}

    getSportsBooksReviewMethodology(): Observable<Record<string, any>> {
        // TODO
		return of({});
    }


	getLatestArticleSummaries(count: number): Observable<Array<BlogArticle>> {
		console.log('getLatestArticleSummaries');
		
		let rno = Math.random()
		console.time('getLatestArticleSummaries ' + rno)
		const query = {
			"type": "stats-insider-articles",
			"metadata.locale_exclusivity": {"$in": ["All Locales", this.localisationService.locale + " Exclusive", ""]}
		}
		return this.http.get<any>(`https://api.cosmicjs.com/v3/buckets/${this.bucketSlug}/objects?`
			+ `query=${JSON.stringify(query).replace(/\{/g, "%7B").replace(/\}/g, "%7D")}`
			+ `&limit=${count}&read_key=${this.readKey}&sort=-published_at&depth=1`)
		.pipe(
			map((response: any) => {
				if (response.objects) {
					// return (response.data as Array<BlogArticle>)
					// 	.sort((a,b) => compareDesc(
					// 		parseJSON(a.published_date),
					// 		parseJSON(b.published_date)
					// 	));
					console.log("latest article summaries retrieved:")
					console.timeLog('getLatestArticleSummaries '+rno)

					const articles = response.objects;

					console.log("latest article summaries sorted:")
					console.timeLog('getLatestArticleSummaries '+rno)
					const transformedArticles = articles
						.map(o => {
								
							const combinedDate = parseISO(o.published_at);
							return {
								// TODO deal with autocon case
								article_category: "dimers_content",
								author: o.metadata.author.title,
								authors: [{
									id: o.metadata.author.id,
									last_name: (o.metadata.author.metadata.author_first_name && o.metadata.author.title.includes(o.metadata.author.metadata.author_first_name)) ? o.metadata.author.title.replace(o.metadata.author.metadata.author_first_name, "").trim() : o.metadata.author.title,
									slug: o.metadata.author.slug,
									first_name: (o.metadata.author.metadata.author_first_name && o.metadata.author.title.includes(o.metadata.author.metadata.author_first_name)) ? o.metadata.author.metadata.author_first_name : "",
									social_username: o.metadata.author.metadata.twitter_username,
									description: o.metadata.author.content,
									short_bio: o.metadata.author.content,
									social_summary_title: o.metadata.author.title + " - Sports Contributor",
									social_summary_description: `The latest articles by ${o.metadata.author.title} on Stats Insider. Read data-driven sports content and news from the home of sports predictions.`,
									thumbnail: {
										dynamic_url: o.metadata.author.metadata.author_thumbnail.imgix_url,
									},
								}],
								content_description: o.content,
								created_at: o.created_at,
								featured_article: null,
								id: o.id,
								published_date: combinedDate.toJSON(),
								last_updated: o.published_at,
								published_date_readable: formatDistance(combinedDate, new Date()) + " ago",
								unpublished_at: o.unpublish_at,
								short_title: o.title,
								slug: o.slug,
								socialThumbnail: {
									url: o.metadata.hero_image.url,
								},
								social_summary_description: o.metadata.preview_text,
								social_summary_title: o.title + "",
								sport_betting_category: [],
								summarized_description: o.metadata.preview_text,
								tags: o.metadata.categories.map(c => ({
									name: c,
									slug: c,
								})),
								thumbnail: {
									dynamic_url: o.metadata.hero_image.imgix_url,
									url: o.metadata.hero_image.url,
								},
								title: o.title,
								faqs: [],
								show_faq: false,
							}
						});
					
					console.log("latest article summaries transformed:")
					console.timeLog('getLatestArticleSummaries '+rno);
					return transformedArticles;
				} else if (response.errors) {
					throw new Error(response.errors[0].message)
				} else {
					throw new Error("UNEXPECTED_FORMAT_OR_ERROR");
				}
			}),
			catchError(this.handleError<Array<BlogArticle>>())
		)
	}

	// TODO
	getBuilderPage(pageCode: string): Observable<LandingPage> {
		console.log('getBuilderPage');
		return of(null);
	}

	// TODO
	builderPageExists(pageCode: string): Observable<boolean> {
		console.log('builderPageExists');
		return of(false);
	}

    //excludeFeature parameter only used for SI(for horse radcing home page use only)
	// excludeFeature parameter is now ignored as featured designation is set based on category
	getLatestArticleSummariesByCategory(category: string, count: number, exceptID?: number, excludeFeature?: boolean): Observable<Array<BlogArticle>> {
		console.log('getLatestArticleSummariesByCategory');

		const queryExclFeature = {
			"type": "stats-insider-articles",
			"metadata.categories": category.toUpperCase(),
			// "metadata.nav_featured": {
			//     $ne: "Yes"
			// }
		}

        const query = {
			"type": "stats-insider-articles",
			"metadata.categories": category.toUpperCase()
		}

		return this.http.get<any>(`https://api.cosmicjs.com/v3/buckets/${this.bucketSlug}/objects?`
			+ `query=${JSON.stringify(excludeFeature ? queryExclFeature : query).replace(/\{/g, "%7B").replace(/\}/g, "%7D")}`
			+ `&limit=${count}&read_key=${this.readKey}&sort=-published_at&depth=1`)
		.pipe(
			map((response: any) => {
				if (response.objects) {
					// return (response.data as Array<BlogArticle>)
					// 	.sort((a,b) => compareDesc(
					// 		parseJSON(a.published_date),
					// 		parseJSON(b.published_date)
					// 	));
					return response.objects
						.map(o => {
							let combinedDate = parseISO(o.published_at);
							return {
								// TODO deal with autocon case
								article_category: "dimers_content",
								author: o.metadata.author.title,
								authors: [{
									id: o.metadata.author.id,
									last_name: (o.metadata.author.metadata.author_first_name && o.metadata.author.title.includes(o.metadata.author.metadata.author_first_name)) ? o.metadata.author.title.replace(o.metadata.author.metadata.author_first_name, "").trim() : o.metadata.author.title,
									slug: o.metadata.author.slug,
									first_name: (o.metadata.author.metadata.author_first_name && o.metadata.author.title.includes(o.metadata.author.metadata.author_first_name)) ? o.metadata.author.metadata.author_first_name : "",
									social_username: o.metadata.author.metadata.twitter_username,
									description: o.metadata.author.content,
									short_bio: o.metadata.author.content,
									social_summary_title: o.metadata.author.title + " - Sports Contributor",
									social_summary_description: `The latest articles by ${o.metadata.author.title} on Stats Insider. Read data-driven sports content and news from the home of sports predictions.`,
									thumbnail: {
										dynamic_url: o.metadata.author.metadata.author_thumbnail.imgix_url,
									},
								}],
								content_description: o.content,
								created_at: o.created_at,
								featured_article: this.autoFeatureSports.some(c => o.metadata.categories.includes(c)),
								id: o.id,
								published_date: combinedDate.toJSON(),
								last_updated: o.published_at,
								published_date_readable: formatDistance(combinedDate, new Date()) + " ago",
								unpublished_at: o.unpublish_at,
								short_title: o.title,
								slug: o.slug,
								socialThumbnail: {
									url: o.metadata.hero_image.url,
								},
								social_summary_description: o.metadata.preview_text,
								social_summary_title: o.title + "",
								sport_betting_category: [],
								summarized_description: o.metadata.preview_text,
								tags: o.metadata.categories.map(c => ({
									name: c,
									slug: c,
								})),
								thumbnail: {
									dynamic_url: o.metadata.hero_image.imgix_url,
									url: o.metadata.hero_image.url,
								},
								title: o.title,
								faqs: [],
								show_faq: false,
							}
						});
				} else if (response.errors) {
					throw new Error(response.errors[0].message)
				} else {
					throw new Error("UNEXPECTED_FORMAT_OR_ERROR");
				}
			}),
			catchError(this.handleError<Array<BlogArticle>>())
		)
	}

	getFeaturedArticleSummaries(count: number, category?: string): Observable<Array<BlogArticle>> {
		// console.log('getFeaturedArticleSummaries');
		let rno = Math.random()
		// console.time('getFeaturedArticleSummaries ' + rno)
		const query = {
			"type": "stats-insider-articles",
			// "metadata.nav_featured": "Yes",

			// if no category selected, articles are deemed featured if they are in one of the "auto-feature" sports
			"metadata.categories": category ? category.toUpperCase() : {
				"$in": this.autoFeatureSports
			},
			"metadata.locale_exclusivity": {"$in": ["All Locales", this.localisationService.locale + " Exclusive", ""]}
		}
		return this.http.get<any>(`https://api.cosmicjs.com/v3/buckets/${this.bucketSlug}/objects?`
			+ `query=${JSON.stringify(query).replace(/\{/g, "%7B").replace(/\}/g, "%7D")}&props=title,slug,published_at,metadata`
			+ `&limit=${count}&read_key=${this.readKey}&sort=-published_at&depth=1`)
		
		.pipe(
			map((response: any) => {
				// console.log(response)

				// let span = tracer.startSpan("expensive-query");

				if (response.objects) {
					this.honeycomb.sendMessage({
						message: `finished retrieval from blog service`
					});
			
					console.log("featured article summaries retrieved:")
					// console.timeLog('getFeaturedArticleSummaries '+rno)

					const articles = response.objects

					console.log("featured article summaries sorted:")
					// console.timeLog('getFeaturedArticleSummaries '+rno)
					const transformedArticles = articles
						.map(o => {
								
							const combinedDate = parseISO(o.published_at);
							return {
								// TODO deal with autocon case
								article_category: "dimers_content",
								author: o.metadata.author.title,
								authors: [{
									id: o.metadata.author.id,
									last_name: (o.metadata.author.metadata.author_first_name && o.metadata.author.title.includes(o.metadata.author.metadata.author_first_name)) ? o.metadata.author.title.replace(o.metadata.author.metadata.author_first_name, "").trim() : o.metadata.author.title,
									slug: o.metadata.author.slug,
									first_name: (o.metadata.author.metadata.author_first_name && o.metadata.author.title.includes(o.metadata.author.metadata.author_first_name)) ? o.metadata.author.metadata.author_first_name : "",
									social_username: o.metadata.author.metadata.twitter_username,
									description: o.metadata.author.content,
									short_bio: o.metadata.author.content,
									social_summary_title: o.metadata.author.title + " - Sports Contributor",
									social_summary_description: `The latest articles by ${o.metadata.author.title} on Stats Insider. Read data-driven sports content and news from the home of sports predictions.`,
									thumbnail: {
										dynamic_url: o.metadata.author.metadata.author_thumbnail.imgix_url,
									},
								}],
								content_description: o.content,
								created_at: o.created_at,
								featured_article: null,
								id: o.id,
								published_date: combinedDate.toJSON(),
								last_updated: o.published_at,
								published_date_readable: formatDistance(combinedDate, new Date()) + " ago",
								unpublished_at: o.unpublish_at,
								short_title: o.title,
								slug: o.slug,
								socialThumbnail: {
									url: o.metadata.hero_image.url,
								},
								social_summary_description: o.metadata.preview_text,
								social_summary_title: o.title + "",
								sport_betting_category: [],
								summarized_description: o.metadata.preview_text,
								tags: o.metadata.categories.map(c => ({
									name: c,
									slug: c,
								})),
								thumbnail: {
									dynamic_url: o.metadata.hero_image.imgix_url,
									url: o.metadata.hero_image.url,
								},
								title: o.title,
								faqs: [],
								show_faq: false,
							}
						}) as Array<BlogArticle>;
					
					console.log("featured article summaries converted:")
					// console.timeLog('getFeaturedArticleSummaries '+rno);
					return transformedArticles;
					// .sort((a,b) => a.featured_article?.order_index - b.featured_article?.order_index);
				} else if (response.errors) {
					throw new Error(response.errors[0].message)
				} else {
					throw new Error("UNEXPECTED_FORMAT_OR_ERROR");
				}
			}),
            catchError(error => {
                if (error.status === 404) {
                    return of([]); // Return empty data for 404 errors
                }
                  throw error;
            })
		)
	}

	getAuthors(): Observable<Array<BlogAuthor>> {
		console.log('getAuthors');
		const query = {
			"type": "authors",
		}
		return this.http.get<any>(`https://api.cosmicjs.com/v3/buckets/${this.bucketSlug}/objects?`
			+ `query=${JSON.stringify(query).replace(/\{/g, "%7B").replace(/\}/g, "%7D")}`
			+ `&read_key=${this.readKey}&props=id,title,slug,content,metadata&depth=1`)
		.pipe(
			map((response: any) => {
				if (response.objects) {
					return response.objects.filter(a => (a.metadata.writes_for || []).includes("Stats Insider")).map((o: Record<string, any>) => ({
						id: o.id,
						last_name: (o.metadata.author_first_name && o.title.includes(o.metadata.author_first_name)) ? o.title.replace(o.metadata.author_first_name, "").trim() : o.title,
							slug: o.slug,
							first_name: (o.metadata.author_first_name && o.title.includes(o.metadata.author_first_name)) ? o.metadata.author_first_name : "",
							social_username: o.metadata.twitter_username,
						description: o.content,
						short_bio: o.content,
						social_summary_title: o.title + " - Sports Contributor",
						featured_candidate: (o.metadata.featured_candidate || []).includes("Yes"),
						social_summary_description: `The latest articles by ${o.title} on Stats Insider. Read data-driven sports content and news from the home of sports predictions.`,
						thumbnail: {
							dynamic_url: o.metadata.author_thumbnail.imgix_url,
						},
					}));
				} else if (response.errors) {
					throw new Error(response.errors[0].message)
				} else {
					throw new Error("UNEXPECTED_FORMAT_OR_ERROR");
				}
			}),
			catchError(this.handleError<Array<BlogAuthor>>())
		)
	}

	getAuthor(slug: string): Observable<BlogAuthor> {
		console.log('getAuthor');
		const query = {
			"type": "authors",
			"slug": slug,
		}
		return this.http.get<any>(`https://api.cosmicjs.com/v3/buckets/${this.bucketSlug}/objects?query=%7B%22type%22%3A%22authors%22%2C%22slug%22%3A%22${slug}%22%7D`
			+ `&read_key=${this.readKey}&props=id,slug,title,content,metadata&depth=1`)
		.pipe(
			map((response: any) => {
				if (response.objects?.length > 0) {
					let author = response.objects[0];
					return {
						id: author.id,
						last_name: (author.metadata.author_first_name && author.title.includes(author.metadata.author_first_name)) ? author.title.replace(author.metadata.author_first_name, "").trim() : author.title,
						slug: author.slug,
						first_name: (author.metadata.author_first_name && author.title.includes(author.metadata.author_first_name)) ? author.metadata.author_first_name : "",
						social_username: author.metadata.twitter_username,
						description: author.content,
						short_bio: author.content,
						social_summary_title: author.title + " - Sports Contributor",
						social_summary_description: `The latest articles by ${author.title} on Stats Insider. Read data-driven sports content and news from the home of sports predictions.`,
						thumbnail: {
							dynamic_url: author.metadata.author_thumbnail.imgix_url,
						},
					};
				} else if (response.errors) {
					throw new Error(response.errors[0].message)
				} else {
					throw new Error("UNEXPECTED_FORMAT_OR_ERROR");
				}
			}),
			catchError(this.handleError<BlogAuthor>())
		)
	}

	getArticleSummariesPage(authorID: string, category: string, query: string, skip: number = 0, count: number): Observable<{articles: Array<BlogArticle>, totalCount: number}> {
		const queryObject = {
			"type": "stats-insider-articles",
			"metadata.author": authorID ? authorID : undefined,
			"metadata.categories": category ? category.toUpperCase() : undefined,
			"metadata.locale_exclusivity": {"$in": ["All Locales", this.localisationService.locale + " Exclusive", ""]},
			"$or": query ? [
				{"title": {"$regex": query, "$options": "i"}},
				{"content": {"$regex": query, "$options": "i"}}
			] : undefined,
		}
		return this.http.get<any>(`https://api.cosmicjs.com/v3/buckets/${this.bucketSlug}/objects?`
			+ `limit=${count}&query=${JSON.stringify(queryObject).replace(/\{/g, "%7B").replace(/\}/g, "%7D")}&props=title,slug,published_at,metadata`
			+ `&read_key=${this.readKey}&sort=-published_at&skip=${skip}&depth=1`)
		.pipe(
			map((response: any) => {
				if (response && response.objects && response.total) {
                    
					return {articles: (response.objects
                        .map(o => {
							
						const combinedDate = parseISO(o.published_at);
						return {
							// TODO deal with autocon case
							article_category: "dimers_content",
							author: o.metadata.author.title,
							authors: [{
								id: o.metadata.author.id,
								last_name: (o.metadata.author.metadata.author_first_name && o.metadata.author.title.includes(o.metadata.author.metadata.author_first_name)) ? o.metadata.author.title.replace(o.metadata.author.metadata.author_first_name, "").trim() : o.metadata.author.title,
								slug: o.metadata.author.slug,
								first_name: (o.metadata.author.metadata.author_first_name && o.metadata.author.title.includes(o.metadata.author.metadata.author_first_name)) ? o.metadata.author.metadata.author_first_name : "",
								social_username: o.metadata.author.metadata.twitter_username,
								description: o.metadata.author.content,
								short_bio: o.metadata.author.content,
								social_summary_title: o.metadata.author.title + " - Sports Contributor",
								social_summary_description: `The latest articles by ${o.metadata.author.title} on Stats Insider. Read data-driven sports content and news from the home of sports predictions.`,
								thumbnail: {
									dynamic_url: o.metadata.author.metadata.author_thumbnail.imgix_url,
								},
							}],
							content_description: o.content,
							created_at: o.created_at,
							featured_article: null,
							id: o.id,
							published_date: combinedDate.toJSON(),
							last_updated: o.published_at,
							published_date_readable: formatDistance(combinedDate, new Date()) + " ago",
							unpublished_at: o.unpublish_at,
							short_title: o.title,
							slug: o.slug,
							socialThumbnail: {
								url: o.metadata.hero_image.url,
							},
							social_summary_description: o.metadata.preview_text,
							social_summary_title: o.title + "",
							sport_betting_category: null,
							summarized_description: o.metadata.preview_text,
							tags: o.metadata.categories.map(c => ({
								name: c,
								slug: c,
							})),
							thumbnail: {
								dynamic_url: o.metadata.hero_image.imgix_url,
								url: o.metadata.hero_image.url,
							},
							title: o.title,
							faqs: [],
							show_faq: false,
						}
					}) as Array<BlogArticle>), totalCount: (response.total as number)};
				} else if (response && (response.objects === null || !response.total)) {
					return {articles: [], totalCount: 0};
				} else if (response.errors) {
					throw new Error(response.errors[0].message)
				} else {
					throw new Error("UNEXPECTED_FORMAT_OR_ERROR");
				}
			}),
			catchError(this.handleError<{articles: Array<BlogArticle>, totalCount: number}>())
		)
	}

	getAuthorSummariesPage(authorSlug: string, skip: number, count: number) {
		return of([]);
	}

	// TODO
	getAdhesiveBanner(stateCode: string): Observable<Record<string, any>> {
		console.log('getAdhesiveBanner');
		return of({});
	}

	// getLatestArticleCategories(): Observable<Array<string>> {
	// 	return this.http.get<any>(`https://api.cosmicjs.com/v3/buckets/${this.bucketSlug}/objects?query=%7B%22type%22%3A%22stats-insider-articles%22%7D&limit=1&read_key=${this.readKey}&props=metafields`)
	// 	.pipe(
	// 		map((response: any) => {
	// 			if (response && response.objects && response.objects[0]
	// 				&& response.objects[0].metafields && response.objects[0].metafields.some(m => m.key === "categories")) {
	// 				return response.objects[0].metafields.find(m => m.key === "categories").options.map(o => o.value);
	// 			} else if (response.errors) {
	// 				throw new Error(response.errors[0].message)
	// 			} else {
	// 				throw new Error("UNEXPECTED_FORMAT_OR_ERROR");
	// 			}
	// 		}),
	// 		catchError(this.handleError<Array<Record<string, any>>>())
	// 	)
	// }

	// TODO
	getWelcomeOffers(state: string, count?: number): Observable<Array<BookOffer>> {
		console.log('getWelcomeOffers');
		return of([]);
	}

	// TODO
	getBestOffers(count: number, state: string, sportCode?: string): Observable<Array<BookOffer>> {
		console.log('getBestOffers');
		return of([]);
	}

	// TODO
	getUserLocationData(): Observable<UserLocationData> {
		return of(null);
	}

	getAppSettings(pageCode: string): Observable<Record<string, any>> {
		let rno = Math.random()
		console.log('getAppSettings');
		console.time('getAppSettings' + rno);
		return this.http.get<any>(`https://api.cosmicjs.com/v3/buckets/${this.bucketSlug}/objects?query=%7B%22type%22%3A%22page-meta-objects%22%2C%22slug%22%3A%22${pageCode}%22%7D&read_key=${this.readKey}&props=slug,title,content,metadata&depth=1`)
		.pipe(
			map((response: any) => {
				if (response.objects) {
					console.log("Page meta returned:")
					console.timeLog('getAppSettings' + rno)
					const returnedObject = {
						...response.objects[0],
						...response.objects[0].metadata,
						// app_title: response.objects[0].title,
						page_title: response.objects[0].title,
						thumbnail: {
							url: response.objects[0].metadata?.meta_image?.url || undefined,
						}
					};
					console.log("Page meta converted:");
					console.timeLog('getAppSettings' + rno)
					return returnedObject;
				} else if (response.errors) {
					throw new Error(response.errors[0].message)
				} else {
					throw new Error("UNEXPECTED_FORMAT_OR_ERROR");
				}
			}),
			catchError(this.handleError<Record<string, any>>())
		)
	}

	getBetHubSettings(sportCode: string): Observable<Record<string, any>> {
		return this.getAppSettings(`${sportCode.toLowerCase()}-predictions`)
			.pipe(
				map(settings => ({
					...settings,
					// site_title: settings.app_title,
					// seo_scheduled_title: settings.seo_title,
				}))
			)
	}

	getMatchMeta(matchID: string): Observable<Record<string, any>> {
		console.log('getMatchMeta');
		let rno = Math.random()
		console.time('getMatchMeta' + rno)
		return this.http.get<any>(`${environment.apiDomain}/pre/${matchID}`)
		.pipe(
			map((match: Match) => {
				console.timeLog('getMatchMeta' + rno)
				let title: string, description: string, match_subheading_title: string, match_subheader: string, match_subheading_description: string;
				let faqs: Array<{headerText: string, bodyHTML: string}> = [];
				const matchName = match.MatchData.Sport === "TEN" ?
					`${match.MatchData.PlayerData.player1.nameDetails.first} ${match.MatchData.PlayerData.player1.nameDetails.last} vs. ${match.MatchData.PlayerData.player2.nameDetails.first} ${match.MatchData.PlayerData.player2.nameDetails.last}`
					: `${this.generalService.teamNameDisplay(match.MatchData.HomeTeam)} vs. ${this.generalService.teamNameDisplay(match.MatchData.AwayTeam)}`
				
				if (match.MatchData.Sport === "TEN") {
					title = `${match.MatchData.PlayerData.player1.nameDetails.first} ${match.MatchData.PlayerData.player1.nameDetails.last} `
						+ `vs ${match.MatchData.PlayerData.player2.nameDetails.first} ${match.MatchData.PlayerData.player2.nameDetails.last} - `
						+ `${match.MatchData.TournamentName} Tennis Match Predictions`;

					description = `Match predictions: ${this.localisationService.localeTerm('h2h_short', '')}, set and game probabilities for ${match.MatchData.TournamentName} `
						+ `${match.MatchData.PlayerData.player1.nameDetails.first} ${match.MatchData.PlayerData.player1.nameDetails.last} ` 
						+ `vs ${match.MatchData.PlayerData.player2.nameDetails.first} ${match.MatchData.PlayerData.player2.nameDetails.last}.`;
				} else if (match.MatchData.Sport === "SOO") {
					title = `${match.MatchData.HomeTeam.DisplayName} vs ${match.MatchData.AwayTeam.DisplayName} State of Origin Game ${match.MatchData.RoundNumber} ${match.MatchData.Season}`;
					description = `It's Queensland vs New South Wales in rugby league's most famous rivalry. See ${this.localisationService.localeTerm('h2h_short', '')}, `
						+ `${this.localisationService.localeTerm('line', '')} and ${this.localisationService.localeTerm('total', '')} probabilities for `
						+ `Game ${match.MatchData.RoundNumber}.`;
					match_subheader = `Stats Insider's State of Origin predictions for the game between ${match.MatchData.HomeTeam.Abv} and ${match.MatchData.AwayTeam.Abv} on ${format(parseJSON(match.MatchData.Date), "iiii, MMMM d, yyyy")}, are featured below, including our best bets, try scorer probabilities, and the latest odds.`;
				} else if (["AFL","AFLW"].includes(match.MatchData.Sport)) {
					title = `${match.MatchData.HomeTeam.DisplayName} vs ${match.MatchData.AwayTeam.DisplayName}`
						+ ` - ${match.MatchData.Season} Round ${match.MatchData.RoundNumber} ${match.MatchData.Sport} Match Predictions`;

					description = `Match predictions: ${this.localisationService.localeTerm('h2h_short', '')}, ${this.localisationService.localeTerm('line', '')} and ${this.localisationService.localeTerm('total', '')} probabilities for ${match.MatchData.Season} `
						+ `${match.MatchData.Sport} Round ${match.MatchData.RoundNumber} `
						+ `${match.MatchData.HomeTeam.DisplayName} vs ${match.MatchData.AwayTeam.DisplayName}.`
					match_subheader = `After simulating ${matchName} on ${format(parseJSON(match.MatchData.Date), "iii MMM d, yyyy")} 10,000 times, Stats Insider's <strong>predictions</strong> and <strong>picks</strong>, including <a routerLink='/best-bets'>best bets</a>, <strong>betting odds</strong> and <a routerLink='/live-now'>live updates</a> can be found below.`;
				} else if (["NRL"].includes(match.MatchData.Sport)) {
					title = `${match.MatchData.HomeTeam.DisplayName} vs ${match.MatchData.AwayTeam.DisplayName}`
						+ ` - ${match.MatchData.Season} Round ${match.MatchData.RoundNumber} ${match.MatchData.Sport} Match Predictions`;

					description = `Match predictions: ${this.localisationService.localeTerm('h2h_short', '')}, ${this.localisationService.localeTerm('line', '')} and ${this.localisationService.localeTerm('total', '')} probabilities for ${match.MatchData.Season} `
						+ `${match.MatchData.Sport} Round ${match.MatchData.RoundNumber} `
						+ `${match.MatchData.HomeTeam.DisplayName} vs ${match.MatchData.AwayTeam.DisplayName}.`
					match_subheader = `Stats Insider has simulated ${matchName} on ${format(parseJSON(match.MatchData.Date), "iii MMM d, yyyy")} 10,000 times to provide <strong>predictions</strong> and <strong>picks</strong>, including <a routerLink='/best-bets'>best bets</a>, <strong>betting odds</strong> and <a routerLink='/live-now'>live updates</a>.`;
				} else if (match.MatchData.Sport === "BBL") {
					title = `${match.MatchData.HomeTeam.DisplayName} vs ${match.MatchData.AwayTeam.DisplayName} - BBL14 Match Predictions`;
					description = `Match predictions and live win probabilities for ${match.MatchData.Sport} `
						+ `${match.MatchData.HomeTeam.DisplayName} vs ${match.MatchData.AwayTeam.DisplayName}.`
				} else {
					title = `${match.MatchData.HomeTeam.DisplayName} vs ${match.MatchData.AwayTeam.DisplayName} `
						+ `- ${match.MatchData.Sport === "ESP" ? "La Liga" : (match.MatchData.Sport === "ALG" ? "A-League" : match.MatchData.Sport)} Match Predictions`;

					if (["NFL", "CFB"].includes(match.MatchData.Sport)) {
						description = `Match predictions: ${this.localisationService.localeTerm('h2h_short', '')}, ${this.localisationService.localeTerm('line', '')} and ${this.localisationService.localeTerm('total', '')} probabilities for ${match.MatchData.Season} `
							+ `${match.MatchData.Sport === "CFB" ? "College Football" : match.MatchData.Sport} Week ${match.MatchData.RoundNumber} `
							+ `${match.MatchData.HomeTeam.DisplayName} vs ${match.MatchData.AwayTeam.DisplayName}.`
					} else if (match.MatchData.Sport === "MLB") {
						description = `Match predictions: ${this.localisationService.localeTerm('h2h_short', '')}, ${this.localisationService.localeTerm('line_mlb', '')} and ${this.localisationService.localeTerm('total', '')} probabilities for MLB `
							+ `${match.MatchData.HomeTeam.DisplayName} vs ${match.MatchData.AwayTeam.DisplayName}.`;
					} else if (["EPL", "ESP", "ALG"].includes(match.MatchData.Sport)) {
						description = `Match predictions: Result, correct score and ${this.localisationService.localeTerm('total', '')} probabilities for `
							+ `${match.MatchData.Sport === "ESP" ? "La Liga" : (match.MatchData.Sport === "ALG" ? "A-League" : match.MatchData.Sport)} `
							+ `matchweek ${match.MatchData.RoundNumber} ${match.MatchData.HomeTeam.DisplayName} vs ${match.MatchData.AwayTeam.DisplayName}.`;
					} else if (match.MatchData.Sport === "NBA") {
						description = `Match predictions: Result, ${this.localisationService.localeTerm('line', '')} and ${this.localisationService.localeTerm('total', '')} probabilities for NBA gameday ${match.MatchData.RoundNumber} `
							+ `${match.MatchData.HomeTeam.DisplayName} vs ${match.MatchData.AwayTeam.DisplayName}.`;
					} else {
						description = `Match predictions: ${this.localisationService.localeTerm('h2h_short', '')}, ${this.localisationService.localeTerm('line', '')} and ${this.localisationService.localeTerm('total', '')} probabilities for ${match.MatchData.Sport} `
							+ `${match.MatchData.HomeTeam.DisplayName} vs ${match.MatchData.AwayTeam.DisplayName}.`;
					}
				}

				
				return {
					title: title,
					match_id: match.MatchData.SIMatchID,
					match_header: match.MatchData.Sport === "SOO" ? `${matchName} Game ${match.MatchData.RoundNumber}` : `${matchName} Predictions`,
					match_subheading_title: match_subheading_title || null,
					match_subheader: match_subheader || `<strong>Predictions</strong> and <strong>picks</strong> for ${matchName} on ${format(parseJSON(match.MatchData.Date), "iii MMM d, yyyy")}, including <a routerLink='/best-bets'>best bets</a>, <strong>betting odds</strong> and <a routerLink='/live-now'>live updates</a>.`,
					match_subheading_description: match_subheading_description || null,
					default_description: description,
					home_team_name: match.MatchData.Sport === "TEN" ? `${match.MatchData.PlayerData.player1.nameDetails.first} ${match.MatchData.PlayerData.player1.nameDetails.last}` : match.MatchData.HomeTeam.DisplayName,
					visiting_team_name: match.MatchData.Sport === "TEN" ? `${match.MatchData.PlayerData.player2.nameDetails.first} ${match.MatchData.PlayerData.player2.nameDetails.last}` : match.MatchData.AwayTeam.DisplayName,
					match_date: match.MatchData.Date,
					venue: match.MatchData.Sport === "TEN" ? match.MatchData.TournamentName : match.MatchData.Venue,
					description: description,
					// thumbnail: {url: "https://cdn.ciphersports.io/images/generic_match_page_meta.jpg"},
					thumbnail: {url: environment.defaultMetaImage},
					faqs: faqs,
				}
			}),
			catchError(this.handleError<Record<string, any>>())
		)
	}

	// TODO
	getBestOddsMeta(): Observable<Array<Record<string, any>>> {
		console.log('getBestOddsMeta');
		return of([]);
	}

	getNewsMeta(sportCode: string): Observable<Record<string, any>> {
		return this.getAppSettings(sportCode.toLowerCase() === "all" ? "news" : `${sportCode.toLowerCase()}-news`);
	}

	getFuturesMeta(sportCode: string): Observable<Record<string, any>> {
		return this.getAppSettings(`${sportCode.toLowerCase()}-futures`);
	}

	
	// this is for sport-specific version of best bets page
	getSportBestBetsMeta(sportCode: string): Observable<Record<string, any>> {
		return this.getAppSettings(`${sportCode.toLowerCase()}-best-bets`);
	}

	// this is for sport-specific version of best props page 
	// TODO
	getSportBestPropsMeta(sportCode: string): Observable<Record<string, any>> {
		return of({});
	}

	// TODO
	getStateSettings(stateCode?: string): Observable<Array<Record<string, any>>> {
		console.log('getStateSettings');
		return of([]);
	}

	// TODO
	getBestBooksData(stateCode?: string): Observable<Array<Record<string, any>>> {
		console.log('getBestBooksData');
		return of([]);
	}

	// TODO
	getBookmakerData(bookmakerCode: string, stateCode?: string): Observable<Record<string, any>> {
		console.log('getBookmakerData');
		return of({});
	}

	// TODO
	getBookmakerPromo(id: string): Observable<Record<string, any>> {
		console.log('getBookmakerPromo');
		return of({});
	}

	// TODO
	getHyperlink(id: string): Observable<Record<string, any>> {
		console.log('getHyperlink');
		return of({});
	}

	// TODO
	getPodcastInformation(): Observable<Record<string, any>> {
		console.log('getPodcastInformation');
		return of({});
	}

	// TODO
	getPodcastList(): Observable<Array<Record<string, any>>> {
		console.log('getPodcastList')
		return of([])
	}

	
	// TODO
	getFreeToPlayData(): Observable<Record<string, any>> {
		console.log('getFreeToPlayData');
		return of({});
	}

	// TODO
	addNewsletterEmail(email: string, firstName?: string): Observable<boolean> {
		console.log('getting geo data addNewsletterEmail()');
		return of(false);
	}

	// TODO
	getBettingExplainedCategories(): Observable<Array<Record<string, any>>> {
		console.log('getBettingExplainedCategories()');
		return of([]);
	}

	// TODO
	getBettingExplainedCategory(slug: string): Observable<Record<string, any>> {
		console.log('getBettingExplainedCategory()');
		return of({});

	}

	// TODO
	getMatchPagePromos(matchID: string): Observable<Array<Record<string, any>>> {
		console.log('getMatchPagePromos()');
		return of([]);
	}

	// TODO
	getBettingExplainedArticle(slug: string): Observable<BlogArticle> {
		console.log('getBettingExplainedArticle()');
		return of(null);

	}

	// TODO
	getOnboardingBookmakers(): Observable<Array<Record<string, any>>> {
		console.log('getOnboardingBookmakers()');
		return of([]);
	}

	searchBettingExplainedArticles(searchTerm: string): Observable<Array<BlogArticle>> {
		console.log('searchBettingExplainedArticles()');
		return of([]);
	}

	sessionIncrementer = 1;

	getIncrement(): number {
		console.log('getIncrement()');
		this.sessionIncrementer++;
		return this.sessionIncrementer;
	}

	getSportsBettingPoll(id: string): Observable<Record<string, any>> {
		console.log('getSportsBettingPoll()');
		return of({});
	}

	getResponsiveBanner(id: number): Observable<Record<string, any>> {
		console.log('getResponsiveBanner()');
		return of({});
	}



	// timeAgoString(date: Date): string {
	// 	let hoursago = differenceInMilliseconds(date, new Date()) / (1000 * 3600) * -1;
	// 	if (hoursago < 1) {
	// 		// minutes ago
	// 		var minutesago = hoursago * 60;
	// 		return minutesago.toFixed(0) + (minutesago.toFixed(0) == "1" ? " minute ago" : " minutes ago");
	// 	} else if (hoursago < 24) {
	// 		// hours ago
	// 		return hoursago.toFixed(0) + (hoursago.toFixed(0) == "1" ? " hour ago" : " hours ago");
	// 	} else if (hoursago < (24 * 7)) {
	// 		// days ago
	// 		var daysago = hoursago / 24;
	// 		return daysago.toFixed(0) + (daysago.toFixed(0) == "1" ? " day ago" : " days ago");
	// 	} else if (isThisYear(date)) {
	// 		return format(date, "d MMMM")
	// 	} else {
	// 		return format(date, "d MMMM yyyy")
	// 	}
	// }

	convertCategorySlug(categorySlug: string): string {
		return SPORTS.find(s => s.code === categorySlug.toUpperCase())?.shortName || categorySlug.toUpperCase();
	}

	

	private handleError<T>() {
		return (error: any): Observable<T> => {
			return throwError(new Error("CMS_CALL_FAILED " + JSON.stringify(error, ["message", "arguments", "type", "name"])));
		}
	}

}
